/**
 * tradeExecutionCap.ts
 * Pure utilities for opposite-direction order reservation caps and relational
 * price constraints (SL/TP).
 *
 * Extracted from the TradeDrawer UI IIFE so the UI layer and service layer
 * share identical logic with no duplication.
 *
 * All functions are intentionally pure — no DB calls, no side effects — so they
 * are safe to call from both React render cycles and inside active Dexie transactions.
 *
 * @see TradeExecutionService.logOrder  — service-layer cap guard
 * @see TradeExecutionService.logAmend  — service-layer amend cap guard
 * @see TradeDrawer                     — UI-layer cap display (maxOppositeOrderQty)
 */

import type { TradeExecutionEvent } from '@/types/schema/TradeExecutionEvent';

/**
 * Compute the maximum quantity allowed for a new opposite-direction resting order.
 *
 * A new opposite-direction order can only reserve as much of the position as
 * is not already reserved by existing active opposite-direction orders.
 *
 * Algorithm:
 *   1. Collect all `order-placed` events whose direction differs from positionDirection.
 *   2. Skip cancelled orders (have a linked `order-cancelled` event).
 *   3. Resolve current quantity per order — latest `order-amended` wins; tiebreak by
 *      `occurredAt` desc then `createdAt` desc (matches TradeDrawer sort).
 *   4. Compute filled quantity — sum `quantity` of all `entry-fill`, `partial-exit`,
 *      and `final-exit` events with `targetOrderEventId === order.id`.
 *   5. Skip fully-filled orders (`filledQty >= currentQty`).
 *   6. Sum `currentQty - filledQty` across all remaining active opposite-direction orders.
 *   7. Return `Math.max(0, remainingQty - reservedOppositeQty)`.
 *
 * Clamped to 0 to handle any pre-existing overreservation gracefully.
 *
 * @param events           - All non-tombstoned events for the trade execution (any order).
 * @param positionDirection - Established position direction (from first entry-fill event).
 * @param remainingQty     - Current remaining position quantity (TradeExecution projection).
 * @param orderType        - When provided, only opposite orders of this type are counted
 *                           (per-type cap for SL/TP). When omitted, all opposite orders are
 *                           counted (shared cap — backward compatible with existing callers).
 * @returns Maximum allowed quantity for a new opposite-direction resting order.
 */
export function computeMaxOppositeOrderQty(
  events: TradeExecutionEvent[],
  positionDirection: 'long' | 'short',
  remainingQty: number,
  orderType?: 'limit' | 'stop',
): number {
  const amendments = events.filter(e => e.eventType === 'order-amended');
  const cancellations = events.filter(e => e.eventType === 'order-cancelled');
  const linkedFills = events.filter(
    e =>
      (e.eventType === 'entry-fill' ||
        e.eventType === 'partial-exit' ||
        e.eventType === 'final-exit') &&
      e.targetOrderEventId !== undefined,
  );

  const oppositeOrders = events.filter(
    e =>
      e.eventType === 'order-placed' &&
      e.direction !== undefined &&
      e.direction !== positionDirection &&
      (orderType === undefined || e.orderType === orderType),
  );

  let reservedOppositeQty = 0;

  for (const order of oppositeOrders) {
    // Skip cancelled orders
    if (cancellations.some(e => e.targetOrderEventId === order.id)) continue;

    // Resolve current quantity — latest quantity-carrying amendment wins.
    // Price-only amendments (quantity: undefined) are excluded so a price-only
    // amend after a qty amend does not revert the resolved qty to the original order qty.
    const orderAmends = amendments.filter(e => e.targetOrderEventId === order.id);
    const qtyAmends = orderAmends.filter(e => e.quantity !== undefined);
    const latestQtyAmend =
      qtyAmends.length > 0
        ? qtyAmends.reduce((a, b) => {
            if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
            return a.createdAt > b.createdAt ? a : b;
          })
        : undefined;
    const currentQuantity = latestQtyAmend?.quantity ?? order.quantity;
    if (currentQuantity === undefined) continue;

    // Compute filled quantity for this order
    const filledQuantity = linkedFills
      .filter(e => e.targetOrderEventId === order.id)
      .reduce((sum, e) => sum + (e.quantity ?? 0), 0);

    // Skip fully-filled orders
    if (filledQuantity >= currentQuantity) continue;

    reservedOppositeQty += currentQuantity - filledQuantity;
  }

  return Math.max(0, remainingQty - reservedOppositeQty);
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

/**
 * Resolve active opposite-direction orders of a given type from the event log.
 * An order is "active" when it is not cancelled and not fully filled.
 * Returns an array of { price, quantity } for each active order.
 *
 * "Current price" = latest price-carrying amendment, else original order price.
 *
 * Used by both computeRelationalPriceConstraint and getActiveOppositeOrderPrices.
 */
function resolveActiveOppositeOrders(
  events: TradeExecutionEvent[],
  positionDirection: 'long' | 'short',
  orderType: 'limit' | 'stop',
): Array<{ price: number; remainingQty: number }> {
  const amendments = events.filter(e => e.eventType === 'order-amended');
  const cancellations = events.filter(e => e.eventType === 'order-cancelled');
  const linkedFills = events.filter(
    e =>
      (e.eventType === 'entry-fill' ||
        e.eventType === 'partial-exit' ||
        e.eventType === 'final-exit') &&
      e.targetOrderEventId !== undefined,
  );

  const candidates = events.filter(
    e =>
      e.eventType === 'order-placed' &&
      e.direction !== undefined &&
      e.direction !== positionDirection &&
      e.orderType === orderType,
  );

  const result: Array<{ price: number; remainingQty: number }> = [];

  for (const order of candidates) {
    if (cancellations.some(e => e.targetOrderEventId === order.id)) continue;

    const orderAmends = amendments.filter(e => e.targetOrderEventId === order.id);

    // Resolve current quantity
    const qtyAmends = orderAmends.filter(e => e.quantity !== undefined);
    const latestQtyAmend =
      qtyAmends.length > 0
        ? qtyAmends.reduce((a, b) => {
            if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
            return a.createdAt > b.createdAt ? a : b;
          })
        : undefined;
    const currentQuantity = latestQtyAmend?.quantity ?? order.quantity;
    if (currentQuantity === undefined) continue;

    // Compute filled quantity
    const filledQuantity = linkedFills
      .filter(e => e.targetOrderEventId === order.id)
      .reduce((sum, e) => sum + (e.quantity ?? 0), 0);

    if (filledQuantity >= currentQuantity) continue;

    // Resolve current price
    const priceAmends = orderAmends.filter(e => e.price !== undefined);
    const latestPriceAmend =
      priceAmends.length > 0
        ? priceAmends.reduce((a, b) => {
            if (a.occurredAt !== b.occurredAt) return a.occurredAt > b.occurredAt ? a : b;
            return a.createdAt > b.createdAt ? a : b;
          })
        : undefined;
    const currentPrice = latestPriceAmend?.price ?? order.price;
    if (currentPrice === undefined) continue;

    result.push({ price: currentPrice, remainingQty: currentQuantity - filledQuantity });
  }

  return result;
}

/**
 * Compute the total quantity currently reserved by active opposite-direction exit orders
 * of a given type.
 *
 * Used by `TradeExecutionService.logClose()` for post-fill per-type reservation validation:
 * after writing a fill and any cancel/amend allocations, the reservation for each order type
 * must not exceed the new `remainingPositionQty`.
 *
 * @param events            - All non-tombstoned events for the trade execution.
 * @param positionDirection - Established position direction.
 * @param orderType         - 'stop' to total active SL reservations, 'limit' for TP.
 * @returns Total remaining quantity across all active opposite-direction orders of the given type.
 */
export function computeActiveOppositeOrderReservation(
  events: TradeExecutionEvent[],
  positionDirection: 'long' | 'short',
  orderType: 'limit' | 'stop',
): number {
  return resolveActiveOppositeOrders(events, positionDirection, orderType)
    .reduce((sum, o) => sum + o.remainingQty, 0);
}

/**
 * Return the current prices of all active opposite-direction orders of a given type.
 *
 * Used by TradeDrawer to compute `activeSlPrices` / `activeTpPrices` for UI-level
 * relational price validation when opening the SL/TP placement modal.
 *
 * @param events            - All non-tombstoned events for the trade execution.
 * @param positionDirection - Established position direction.
 * @param orderType         - 'stop' to get active SL prices, 'limit' to get active TP prices.
 * @returns Array of current prices for all active opposite-direction orders of the given type.
 */
export function getActiveOppositeOrderPrices(
  events: TradeExecutionEvent[],
  positionDirection: 'long' | 'short',
  orderType: 'limit' | 'stop',
): number[] {
  return resolveActiveOppositeOrders(events, positionDirection, orderType).map(o => o.price);
}

/**
 * Validate the relational price constraint for a candidate SL or TP exit order.
 *
 * For a long position:
 *   - Placing a TP (limit): candidate price must be strictly above all active SL prices.
 *   - Placing a SL (stop): candidate price must be strictly below all active TP prices.
 * For a short position, the inequalities are reversed:
 *   - Placing a TP (limit): candidate price must be strictly below all active SL prices.
 *   - Placing a SL (stop): candidate price must be strictly above all active TP prices.
 *
 * When no active opponent orders exist, the constraint trivially passes.
 *
 * @param events            - All non-tombstoned events for the trade execution.
 * @param positionDirection - Established position direction.
 * @param candidatePrice    - Price of the order being placed or amended.
 * @param candidateType     - 'stop' for SL, 'limit' for TP.
 * @returns `{ valid: true }` when the constraint is satisfied, or
 *          `{ valid: false, reason: string }` describing the violation.
 */
export function computeRelationalPriceConstraint(
  events: TradeExecutionEvent[],
  positionDirection: 'long' | 'short',
  candidatePrice: number,
  candidateType: 'stop' | 'limit',
): { valid: boolean; reason?: string } {
  const opponentType: 'stop' | 'limit' = candidateType === 'limit' ? 'stop' : 'limit';
  const opponentOrders = resolveActiveOppositeOrders(events, positionDirection, opponentType);

  if (opponentOrders.length === 0) {
    return { valid: true };
  }

  const isLong = positionDirection === 'long';
  const isTP = candidateType === 'limit';
  const orderLabel = isTP ? 'Take Profit' : 'Stop Loss';
  const opponentLabel = isTP ? 'Stop Loss' : 'Take Profit';
  const posLabel = isLong ? 'long' : 'short';

  // mustBeAbove is true when: long + TP (above SL), or short + SL (above TP).
  // Equivalently: mustBeAbove = (isLong === isTP).
  const mustBeAbove = isLong === isTP;

  for (const { price: opponentPrice } of opponentOrders) {
    if (mustBeAbove && candidatePrice <= opponentPrice) {
      return {
        valid: false,
        reason: `${orderLabel} price must be above all active ${opponentLabel} prices (${posLabel} position).`,
      };
    }
    if (!mustBeAbove && candidatePrice >= opponentPrice) {
      return {
        valid: false,
        reason: `${orderLabel} price must be below all active ${opponentLabel} prices (${posLabel} position).`,
      };
    }
  }

  return { valid: true };
}
